import { ReactNode } from "react";
import { X } from "lucide-react";
import { Backdrop } from "./Backdrop";
import { Button } from "./Button";
import { twMerge } from "tailwind-merge";

interface DrawerHeader {
  start?: ReactNode;
  title?: ReactNode;
  subtitle?: ReactNode;
  footer?: ReactNode;
  tabs?: ReactNode;
}

interface DrawerProps {
  open: boolean;
  onClose: VoidFunction;
  children: ReactNode;
  header?: DrawerHeader;
  className?: string;
}

export function Drawer({
  open,
  onClose,
  children,
  header,
  className,
}: DrawerProps) {
  if (!open) return null;

  const hasHeader = header && (header.start || header.title || header.subtitle);

  return (
    <div className="animate-v5-overlay-fade-in fixed inset-0 z-40 flex items-stretch justify-end">
      <Backdrop onClick={onClose} />

      <div className="relative z-1 box-border flex h-full p-3">
        <div
          className={twMerge(
            "animate-v5-tray-slide-in bg-surface-content flex h-full w-[560px] flex-col overflow-hidden rounded-xl shadow-[0_24px_80px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.03)]",
            className,
          )}
        >
          {hasHeader && (
            <div className="shrink-0 border-b bg-white px-5 pt-6 pb-5">
              <div className="flex">
                <div className="flex items-center gap-4">
                  {header.start}
                  <div className="flex min-w-0 flex-1 flex-col gap-1">
                    {header.title}
                    {header.subtitle}
                  </div>
                </div>
                <Button
                  onClick={onClose}
                  color="secondary"
                  icon={X}
                  aria-label="Close"
                  size="lg"
                  className="ml-auto size-[36px] rounded-md"
                />
              </div>
              {header.footer && (
                <div className="mt-3 flex-1 border-t border-[#F3F4F6] pt-3">
                  {header.footer}
                </div>
              )}
              {header.tabs && (
                <div className="-mx-5 mt-3 -mb-5">{header.tabs}</div>
              )}
            </div>
          )}
          <div className="min-h-0 flex-1 overflow-y-auto p-5">{children}</div>
        </div>
      </div>
    </div>
  );
}
